import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/modern/utils';
import { Button } from '@/modern/components/ui/button';
import { Input } from '@/modern/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/modern/components/ui/popover';
import TaskEditorColorMapping from '@/other/constants/TaskEditorColors';

interface ColorPickerProps {
  value?: string | null;
  onChange: (color: string | null) => void;
  clearable?: boolean;
  editable?: boolean;
  className?: string;
  triggerClassName?: string;
  children?: React.ReactNode;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  clearable = false,
  editable = false,
  className,
  triggerClassName,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value || '#000000');

  const handleColorSelect = (color: string | null) => {
    onChange(color);
    if (!editable) {
      setOpen(false);
    }
  };

  const handleCustomColorApply = () => {
    onChange(customColor);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button
            variant="outline"
            className={cn('w-full justify-start', triggerClassName)}
          >
            <div
              className="mr-2 h-5 w-5 rounded border"
              style={{ backgroundColor: value || 'transparent' }}
            />
            {value || 'Select color'}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className={cn('w-80 p-3', className)} align="start">
        <div className="space-y-3">
          {/* Color Grid */}
          <div className="grid grid-cols-10 gap-1">
            {TaskEditorColorMapping.map((colorItem, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'h-7 w-7 rounded border border-border hover:scale-110 transition-transform relative',
                  value === colorItem.color && 'ring-2 ring-primary ring-offset-2'
                )}
                style={{ backgroundColor: colorItem.color }}
                onClick={() => handleColorSelect(colorItem.color)}
                title={colorItem.color}
              >
                {value === colorItem.color && (
                  <Check
                    className="absolute inset-0 m-auto h-4 w-4"
                    style={{ color: colorItem.text_color }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Clear Color Button */}
          {clearable && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleColorSelect(null)}
            >
              <i className="fa-regular fa-eraser mr-2" />
              Remove color
            </Button>
          )}

          {/* Custom Color Input */}
          {editable && (
            <div className="flex gap-2 pt-2 border-t">
              <Input
                type="text"
                placeholder="#000000"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="flex-1"
              />
              <Button type="button" onClick={handleCustomColorApply}>
                Apply
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};